// src/data/claimQueries.js
//
// THE ONE PLACE A CLAIMS LISTING IS SHAPED. Four pages (Dashboard, Claims, LCDesk, GCDesk) each
// built their own role-shaped query and each built the same one:
//
//     gc  ->  where('committee', '==', user.committee)
//     lc  ->  where('scopeKey', 'in', user.scopes)
//
// WALK FINDING F-15 (2026-09-10, task list SS201). Signed in as Christian Eckert (General
// Chairperson, GO-342) the Committee page said "permission-denied: Missing or insufficient
// permissions" - and so did every other listing for every General and Local Chairperson. The
// rules were not wrong; the queries were. Since batch 4 (ruling 56) the claims read arm for an LC
// or GC is
//
//     !isDraftDoc() && (inLCScope(resource.data.scopeKey) || inGCScope(resource.data.committee))
//
// and Firestore does not filter a query against its rules - it PROVES the query. A query that
// only says `committee == GO-342` could return a draft, the rule forbids a draft, so the whole
// query is refused before a single document is read. Ruling 56 was enforced on 2026-08-29 and
// every non-admin listing of claims has been refused since. The single-document reads the rules
// suite asserts were never the shape the pages issue; the suite now asserts the listing too.
//
// THE QUERY MUST STATE WHAT THE RULE REQUIRES. The rule requires "not a draft"; the query says
// `status in <every status that is not draft>`. That is an equality-shaped constraint, provable
// by the rules engine, and served by single-field indexes - no composite index, no -Rules deploy.
//   - A `status != 'draft'` filter would also be provable, but an inequality beside an equality
//     needs a composite index for each pair (committee+status, scopeKey+status).
//   - For a Local Chairperson, `scopeKey in [...]` beside `status in [...]` multiplies the
//     disjunctions (Firestore caps the product at 30), so his listing is ONE QUERY PER TERRITORY
//     merged here, and each is equality + in.
//
// Owner and admin listings are untouched: the owner arm is proved by `createdBy == uid`, and the
// admin arm needs nothing.
//
// SS XI: NON_DRAFT_STATUSES is a projection of the ladder in claimLadder.js plus its off-ladder
// states, never a second list. A status the ladder does not know would be invisible to a
// chairperson here; CLM-1's live status census is what would show one.
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { STAGE_ORDER, OFF_LADDER } from './claimLadder';
import { keepForViewer } from './testTerritory';   // Record 662
import { capScopes } from './scopeCap';   // T5 item 20: the 30-territory cap, in one place

export const NON_DRAFT_STATUSES = STAGE_ORDER.filter(s => s !== 'draft').concat(OFF_LADDER);

// The queries a user may run for a claims listing, in the shape the rules can prove. Exported so
// a test or a verifier can see the shape without subscribing.
export function visibleClaimQueries(db, user) {
  const claimsRef = collection(db, 'claims');
  // Ruling 66 (Record 582): the administrator lists every NON-draft claim plus his own; another person's draft is read
  // only through the audited callable adminReadDraft, so the listing is shaped like an officer's.
  if (user && user.isAdmin) return [query(claimsRef, where('status', 'in', NON_DRAFT_STATUSES)),
    query(claimsRef, where('createdBy', '==', user.uid || '__none__'))];
  if (user && user.role === 'general_chair' && user.committee) {
    return [query(claimsRef, where('committee', '==', user.committee), where('status', 'in', NON_DRAFT_STATUSES))];
  }
  if (user && user.role === 'local_chair' && Array.isArray(user.scopes) && user.scopes.length > 0) {
    // One query per territory. Firestore allows 30 disjunctions per query; the roster's largest
    // territory list is far below that, and the pages already report a list past 30 as truncated.
    return capScopes(user).scopes.map(s =>
      query(claimsRef, where('scopeKey', '==', s), where('status', 'in', NON_DRAFT_STATUSES)));
  }
  // PLAN R-4 (2026-09-20): a member lists the claims he FILED (any status, including his drafts) and the
  // claims that are ABOUT him - by uid and by the e-mail on the claim - which a rule admits only when they
  // are not drafts, so those two queries are shaped that way (the lesson of ruling 182 / F-15).
  const uid = (user && user.uid) || '__none__';
  const mine = query(claimsRef, where('createdBy', '==', uid));
  if (!user || user.role !== 'member') return [mine];
  const about = [query(claimsRef, where('claimantUid', '==', uid), where('status', 'in', NON_DRAFT_STATUSES))];
  if (user.email) about.push(query(claimsRef, where('claimantEmail', '==', user.email), where('status', 'in', NON_DRAFT_STATUSES)));
  return [mine].concat(about);
}

// Subscribe to everything the user may list. onRows receives the merged rows ({ docId, ...data })
// once EVERY query has reported, and again on every change; onError receives the first error.
// Returns the unsubscribe function. Rows are unsorted - each page orders them as it always did.
export function subscribeVisibleClaims(db, user, onRows, onError) {
  const qs = visibleClaimQueries(db, user);
  const perQuery = qs.map(() => null);   // null until that query's first snapshot
  let failed = false;
  const emit = () => {
    if (failed || perQuery.some(m => m === null)) return;
    const merged = new Map();
    for (const m of perQuery) for (const [id, row] of m) merged.set(id, row);
    // Record 662: an EXAMPLE claim (a tester's) is listed for a tester only - never in a real officer's or member's list.
    onRows(keepForViewer(user, Array.from(merged.values())));
  };
  const unsubs = qs.map((q, i) => onSnapshot(q, snap => {
    perQuery[i] = new Map(snap.docs.map(d => [d.id, { docId: d.id, ...d.data() }]));
    emit();
  }, err => {
    if (failed) return;
    failed = true;
    if (onError) onError(err);
  }));
  return () => { for (const u of unsubs) u(); };
}
